import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, MoreVertical, Hourglass, UserMinus, List, LayoutGrid, Check, X, RotateCcw, ClipboardList, Ticket } from 'lucide-react';
import { getRoster, saveAttendance, moveStudentToWaitlist, removeStudentFromClass, RosterStudent, RosterVisitante, UnenrollInfo } from '../api';
import PersonAvatar from '../../../components/PersonAvatar';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import EmptyState from '../../../components/EmptyState';
import MonthChips from './MonthChips';
import StudentTooltip from './StudentTooltip';
import TeacherAttendance, { TeacherAttendanceCard, useTeacherAttendance } from './TeacherAttendance';

const STATUSES: { v: string; label: string; cls: string }[] = [
  { v: 'present', label: 'Asistió', cls: 'bg-emerald-600' },
  { v: 'absent', label: 'Faltó', cls: 'bg-red-600' },
  { v: 'makeup', label: 'Reposición', cls: 'bg-amber-500' },
];

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// `plain`: sin borde/margen superior (cuando el padre ya lo enmarca en su propia tarjeta).
// `onChanged`: bajar a alguien de la clase mueve los contadores de la ficha (inscritos / en espera).
// `reloadKey`: al cobrar una clase suelta desde «Añadir alumno…» la lista se recarga para que salga la visita.
export default function Attendance({ classId, plain = false, onChanged, reloadKey = 0 }: { classId: number; plain?: boolean; onChanged?: () => void; reloadKey?: number }) {
  const toast = useToast();
  // El profe usa este mismo componente en su portal, donde /alumnos/:id no existe → solo admin enlaza.
  const { user } = useAuth();
  const canOpenProfile = user?.role === 'admin';
  // Bajar gente de la clase es de recepción/admin, no del profe en su portal.
  const canManage = user?.role === 'admin';
  const [date, setDate] = useState(todayLocal());
  const [roster, setRoster] = useState<RosterStudent[]>([]);
  const [visitantes, setVisitantes] = useState<RosterVisitante[]>([]);
  const [marks, setMarks] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Asistencia del MAESTRO (un solo estado para la fila de «Lista» y la tarjeta de «Fotos»). Se guarda al tocar;
  // no forma parte de `marks`, así que «Todos asistieron» y «Guardar lista» no la tocan.
  const maestro = useTeacherAttendance(classId, date);

  const load = useCallback(() => {
    setLoading(true);
    getRoster(classId, date)
      .then((r) => {
        setRoster(r.data);
        setVisitantes(r.visitantes || []);
        const m: Record<number, string> = {};
        for (const s of [...r.data, ...(r.visitantes || [])]) if (s.status) m[s.contact_id] = s.status;
        setMarks(m);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, date, reloadKey]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const records = Object.entries(marks).map(([account_id, status]) => ({ account_id: Number(account_id), status }));
      const r = await saveAttendance(classId, date, records);
      toast.success(`Lista guardada · ${r.saved} persona${r.saved === 1 ? '' : 's'}`);
    } catch (e: any) { toast.error(e?.message || 'Error al guardar la lista'); }
    finally { setSaving(false); }
  }

  const markAll = (status: string) => {
    const m: Record<number, string> = {};
    for (const s of [...roster, ...visitantes]) m[s.contact_id] = status;
    setMarks(m);
  };

  // ── Bajar a alguien de la clase (pedido de Karla 29-jul, como Kydemy) ──────────────
  // Dos caminos: a la LISTA DE ESPERA (libera el lugar y puede regresar) o quitarlo de la
  // clase (sigue en las demás). Si queda sin ninguna clase y trae adeudo, se avisa en el acto.
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Vista del roster: lista compacta o galería de fotos (preferencia recordada por navegador).
  const [vista, setVista] = useState<'lista' | 'fotos'>(() =>
    (localStorage.getItem('lm-roster-vista') as 'lista' | 'fotos') || 'lista');
  useEffect(() => { localStorage.setItem('lm-roster-vista', vista); }, [vista]);

  /** Un clic en la foto alterna asistió ↔ faltó (los dos estados que usa el 99% del tiempo). */
  function cicla(contactId: number) {
    setMarks((prev) => {
      const cur = prev[contactId];
      const next = cur === 'present' ? 'absent' : cur === 'absent' ? undefined : 'present';
      const m = { ...prev };
      if (next) m[contactId] = next; else delete m[contactId];
      return m;
    });
  }

  function avisoTrasBaja(nombre: string, info: UnenrollInfo) {
    if (!info.left_without_classes) return;
    const abiertos = info.open_debts || [];
    if (!abiertos.length) { toast.info(`${nombre} ya no está en ninguna clase.`); return; }
    const total = abiertos.reduce((s, d) => s + Number(d.amount || 0), 0);
    toast.info(`${nombre} quedó sin clases y trae ${abiertos.length} adeudo(s) por $${total.toLocaleString('es-MX')}. Revísalos en su ficha para no cobrarle de más.`);
  }

  async function bajarAEspera(s: RosterStudent) {
    const nombre = `${s.first_name} ${s.last_name || ''}`.trim();
    if (!confirm(`¿Pasar a ${nombre} a la lista de espera de esta clase?\n\nSe libera su lugar y queda apuntada para cuando quiera regresar.`)) return;
    setBusyId(s.contact_id); setMenuFor(null);
    try {
      const r = await moveStudentToWaitlist(classId, s.contact_id);
      toast.success(`${nombre} pasó a la lista de espera`);
      avisoTrasBaja(nombre, r.data);
      load(); onChanged?.();
    } catch (e: any) { toast.error(e?.message || 'No se pudo mover a la lista de espera'); }
    finally { setBusyId(null); }
  }

  async function quitarDeClase(s: RosterStudent) {
    const nombre = `${s.first_name} ${s.last_name || ''}`.trim();
    if (!confirm(`¿Quitar a ${nombre} de esta clase?\n\nSi está inscrito en otras, ahí sigue. Su historial de asistencia y pagos no se toca.`)) return;
    setBusyId(s.contact_id); setMenuFor(null);
    try {
      const r = await removeStudentFromClass(classId, s.contact_id);
      toast.success(`${nombre} ya no está en esta clase`);
      avisoTrasBaja(nombre, r.data);
      load(); onChanged?.();
    } catch (e: any) { toast.error(e?.message || 'No se pudo quitar de la clase'); }
    finally { setBusyId(null); }
  }

  const counts = [...roster, ...visitantes].reduce((acc, s) => {
    const v = marks[s.contact_id]; if (v) acc[v] = (acc[v] || 0) + 1; return acc;
  }, {} as Record<string, number>);
  // Alumnos con al menos un mes ya corrido sin pago (los "rojos" del semáforo).
  const sinPagar = roster.filter((s) => (s.months || []).some((m) => m.status === 'due')).length;

  return (
    <div className={plain ? '' : 'mt-6 border-t border-line pt-5'}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-content"><ClipboardList size={15} className="text-accent-ink" /> Pasar lista</h3>
          {/* «Para esta fecha no debe haber rojos» — el conteo se lee sin revisar fila por fila. */}
          {sinPagar > 0 && (
            <span title="Alumnos con un mes ya corrido sin pago" className="rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-600 ring-1 ring-red-500/30 dark:text-red-400">
              {sinPagar} sin pagar
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Lista ↔ Fotos: al inicio de semestre Karla no ubica los nombres, pero SÍ las caras
              («de volada es la foto, te familiarizas, y clic clic clic»). La preferencia se recuerda. */}
          <div className="flex rounded-lg border border-line bg-surface p-0.5">
            {([['lista', List, 'Lista'], ['fotos', LayoutGrid, 'Fotos']] as const).map(([v, Icon, label]) => (
              <button key={v} onClick={() => setVista(v)} title={`Ver en ${label.toLowerCase()}`}
                className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition ${vista === v ? 'bg-accent text-on-accent' : 'text-content-2 hover:bg-surface-2'}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="rounded-lg border border-line bg-surface px-2 py-1.5 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
          <button onClick={() => markAll('present')} className="rounded-lg border border-line bg-surface px-2 py-1.5 text-xs text-content-2 transition hover:bg-surface-2">Todos asistieron</button>
        </div>
      </div>

      {/* Karla 29-sep: la asistencia del maestro va aquí mismo y pasa directo a la nómina. En «Fotos» (Mabel 19-oct)
          es la primera tarjeta de la galería; si no hay galería (sin alumnos) se queda la fila. */}
      {!(vista === 'fotos' && !loading && (roster.length > 0 || visitantes.length > 0)) && <TeacherAttendance ctl={maestro} verFoto={canOpenProfile} />}

      {loading ? <div className="text-sm text-content-3">Cargando roster…</div> : roster.length === 0 && visitantes.length === 0 ? (
        <EmptyState compact icon={Users} title="Sin alumnos inscritos" subtitle="Esta clase aún no tiene alumnos en su lista." />
      ) : (
        <>
          {vista === 'fotos' ? (
            /* ===== GALERÍA DE FOTOS (como Kydemy): cara grande, un clic marca ===== */
            /* items-start: la tarjeta de la maestra es más alta (lleva sus 3 botones) y no debe estirar las de al lado */
            <div className="grid grid-cols-2 items-start gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              <TeacherAttendanceCard ctl={maestro} canOpenProfile={canOpenProfile} />
              {roster.map((s) => {
                const mk = marks[s.contact_id];
                const ring = mk === 'present' ? 'ring-2 ring-emerald-500' : mk === 'absent' ? 'ring-2 ring-red-500' : mk === 'makeup' ? 'ring-2 ring-amber-500' : 'ring-1 ring-line';
                return (
                  <div key={s.contact_id} className={`group relative overflow-hidden rounded-xl bg-surface-2 ${ring}`}>
                    <button onClick={() => cicla(s.contact_id)} aria-label="Un clic: asistió · otro: faltó · otro: sin marcar"
                      className="block w-full text-left">
                      <PersonAvatar contactId={s.contact_id} hasPhoto={s.has_photo}
                        name={s.first_name} lastName={s.last_name}
                        size="aspect-square w-full block" rounded="rounded-none"
                        initialClass="font-display text-4xl"
                        imgClass={`transition ${mk === 'absent' ? 'opacity-40 grayscale' : ''}`}
                        ringClass="fill-surface-2" title="" />
                      {/* Marca de estado, grande y visible de lejos */}
                      {mk && (
                        <span className={`absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full text-white shadow ${mk === 'present' ? 'bg-emerald-600' : mk === 'absent' ? 'bg-red-600' : 'bg-amber-500'}`}>
                          {mk === 'present' ? <Check size={16} /> : mk === 'absent' ? <X size={16} /> : <RotateCcw size={14} />}
                        </span>
                      )}
                      {/* Hueco del nombre (el nombre real va FUERA del botón, abajo): así la franja
                          oscura sigue siendo parte del clic de asistencia */}
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/80 to-transparent" />
                    </button>
                    {/* NOMBRE = disparador del tooltip y enlace a su asistencia (Karla 29-sep-2026:
                        «que esa información la despliegue hasta que ponga el cursor en su nombre… y
                        cuando le dé clic a su nombre me mande su perfil, uno que me ponga la asistencia»).
                        Antes el tooltip salía en TODA la tarjeta y tapaba las caras de al lado. */}
                    <div className="pointer-events-none absolute bottom-1.5 left-2 right-12">
                      <StudentTooltip alumno={s} off={menuFor !== null}>
                        {(() => {
                          const nombre = (
                            <>
                              <span className="block truncate text-[13px] font-semibold text-white">{s.first_name}</span>
                              <span className="block truncate text-[11px] text-white/70">{s.last_name}</span>
                            </>
                          );
                          return canOpenProfile ? (
                            <Link to={`/alumnos/${s.contact_id}?t=asistencia`}
                              className="pointer-events-auto inline-block max-w-full cursor-pointer rounded hover:underline decoration-white/70">
                              {nombre}
                            </Link>
                          ) : <span className="pointer-events-auto inline-block max-w-full">{nombre}</span>;
                        })()}
                      </StudentTooltip>
                    </div>
                    {/* Meses arriba a la izquierda: el semáforo también sirve al pasar lista */}
                    {/* right-10: el semestre son 5 chips y no deben montarse sobre la marca ✓/✗ */}
                    <div className="pointer-events-none absolute left-1.5 right-10 top-1.5">
                      <MonthChips months={s.months} compact />
                    </div>
                    {/* Reposición (poco frecuente) y perfil, sin robarle protagonismo al clic */}
                    <div data-no-tip className="absolute bottom-1.5 right-1.5 flex gap-1 opacity-0 transition group-hover:opacity-100">
                      <button onClick={() => setMarks({ ...marks, [s.contact_id]: 'makeup' })} aria-label="Reposición"
                        className="rounded bg-amber-500/90 px-1.5 py-0.5 text-[10px] font-bold text-white">R</button>
                      {canOpenProfile && (
                        <Link to={`/alumnos/${s.contact_id}`} aria-label="Ver perfil"
                          className="rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white">Ficha</Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <div className="space-y-1.5">
            {roster.map((s) => (
              <div key={s.contact_id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-line bg-surface-2 px-3 py-2">
                {(() => {
                  const identity = (
                    <>
                      <PersonAvatar contactId={s.contact_id} hasPhoto={s.has_photo}
                        name={s.first_name} lastName={s.last_name}
                        size="h-8 w-8" ringClass="fill-surface-2" title="" />
                      <span className="truncate text-sm text-content">{s.first_name} {s.last_name}</span>
                    </>
                  );
                  // Mismo criterio que la vista Fotos: la info sale solo sobre el nombre y el clic
                  // abre su ficha en la pestaña Asistencia.
                  return (
                    <StudentTooltip alumno={s}>
                      {canOpenProfile ? (
                        <Link to={`/alumnos/${s.contact_id}?t=asistencia`} aria-label="Ver asistencia"
                          className="flex min-w-0 flex-1 items-center gap-3 rounded transition hover:opacity-80 [&>span]:hover:underline">
                          {identity}
                        </Link>
                      ) : (
                        <div className="flex min-w-0 flex-1 items-center gap-3">{identity}</div>
                      )}
                    </StudentTooltip>
                  );
                })()}
                {/* Semáforo de cobro (meses del semestre) — pedido de Karla: verlo aquí mismo, sin salir de la clase */}
                <MonthChips months={s.months} />
                {/* data-no-tip: sobre los botones y el menú, el tooltip del alumno se calla. Si no,
                    te sigue con el cursor y tapa justo lo que vas a clicar (reportado por Carlos 30-jul). */}
                <div className="flex gap-1" data-no-tip>
                  {STATUSES.map((st) => (
                    <button key={st.v}
                      onClick={() => setMarks({ ...marks, [s.contact_id]: st.v })}
                      className={`rounded px-2 py-1 text-xs transition ${marks[s.contact_id] === st.v ? `${st.cls} text-white` : 'bg-surface text-content-2 ring-1 ring-line hover:bg-surface-2'}`}>
                      {st.label}
                    </button>
                  ))}
                </div>
                {/* Bajar de la clase sin salir de aquí (antes había que ir alumno por alumno a su ficha) */}
                {canManage && (
                  <div className="relative" data-no-tip>
                    <button onClick={() => setMenuFor(menuFor === s.contact_id ? null : s.contact_id)}
                      disabled={busyId === s.contact_id} aria-label="Más acciones"
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-content-3 transition hover:bg-surface hover:text-content disabled:opacity-40">
                      <MoreVertical size={16} />
                    </button>
                    {menuFor === s.contact_id && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setMenuFor(null)} />
                        {/* .glass-menu: menú OPACO. Con bg-surface (incluso con shadow-xl) el vidrio deja
                            leer los botones de la fila de abajo — ya mordió en la agenda dental. */}
                        <div className="absolute right-0 top-8 z-40 w-56 overflow-hidden glass-menu rounded-xl border border-line shadow-xl">
                          <button onClick={() => bajarAEspera(s)}
                            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-content-2 transition hover:bg-surface-2">
                            <Hourglass size={14} className="text-amber-500" /> Pasar a lista de espera
                          </button>
                          <button onClick={() => quitarDeClase(s)}
                            className="flex w-full items-center gap-2 border-t border-line px-3 py-2.5 text-left text-sm text-content-2 transition hover:bg-surface-2 hover:text-red-500">
                            <UserMinus size={14} className="text-red-500" /> Quitar de la clase
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          )}

          {/* ===== CLASE SUELTA DE HOY (F3, Karla 30-sep): quien pagó clase suelta o muestra de ESTA clase
              para esta fecha, SIN inscribirlo — inscribirlo lo volvía alumno. Sale del pago del día. ===== */}
          {visitantes.length > 0 && (
            <div className="mt-4" data-testid="visitantes">
              <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-content-3">
                <Ticket size={13} className="text-sky-500" /> Clase suelta de este día · {visitantes.length}
              </h4>
              <div className="space-y-1.5">
                {visitantes.map((s) => (
                  <div key={s.contact_id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-2">
                    {(() => {
                      const identity = (
                        <>
                          <PersonAvatar contactId={s.contact_id} hasPhoto={s.has_photo}
                            name={s.first_name} lastName={s.last_name} size="h-8 w-8" ringClass="fill-surface-2" title="" />
                          <span className="min-w-0">
                            <span className="block truncate text-sm text-content">{s.first_name} {s.last_name}</span>
                            <span className="block text-[11px] text-content-3">
                              {s.visita === 'muestra' ? 'Clase muestra (gratis)' : s.visita === 'clase_suelta' ? `Clase suelta · $${Number(s.pagado).toLocaleString('es-MX')}` : 'Sin pago de este día'}
                              {s.es_prospecto ? ' · prospecto' : ' · alumna de otra clase'}
                            </span>
                          </span>
                        </>
                      );
                      return canOpenProfile ? (
                        <Link to={`/alumnos/${s.contact_id}`} className="flex min-w-0 flex-1 items-center gap-3 rounded transition hover:opacity-80">{identity}</Link>
                      ) : <div className="flex min-w-0 flex-1 items-center gap-3">{identity}</div>;
                    })()}
                    <div className="flex gap-1">
                      {STATUSES.filter((st) => st.v !== 'makeup').map((st) => (
                        <button key={st.v} onClick={() => setMarks({ ...marks, [s.contact_id]: st.v })}
                          className={`rounded px-2 py-1 text-xs transition ${marks[s.contact_id] === st.v ? `${st.cls} text-white` : 'bg-surface text-content-2 ring-1 ring-line hover:bg-surface-2'}`}>
                          {st.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-content-3">No están inscritos: salen aquí porque pagaron la clase de este día. Siguen como prospecto aunque les pases lista.</p>
            </div>
          )}

          {vista === 'fotos' && (
            <p className="mt-2 text-[11px] text-content-3">
              Un clic en la foto marca <b className="text-emerald-600 dark:text-emerald-400">asistió</b>, otro <b className="text-red-600 dark:text-red-400">faltó</b> y otro la deja sin marcar · pasa el cursor por el <b>nombre</b> para ver sus clases y lo que debe; clic en el nombre abre su asistencia.
              {maestro.st?.teacher && <> La tarjeta de la <b>maestra/o</b> se guarda al tocarla (no entra en «Todos asistieron» ni en «Guardar lista»).</>}
            </p>
          )}

          {/* Leyenda del semáforo: la primera vez no es obvio qué significan los colores. */}
          {roster.some((s) => s.months?.length) && (
            <div className="mt-2.5 flex flex-wrap items-center gap-3 text-[11px] text-content-3">
              <span className="font-medium">Meses:</span>
              <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-600" /> pagado</span>
              <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> abonó parte</span>
              <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-red-600" /> debe</span>
              <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-white ring-1 ring-slate-300" /> aún no corre</span>
              <span className="text-content-3/70">· pasa el cursor por un mes para ver el detalle</span>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="text-xs text-content-2">
              {counts.present || 0} asistieron · {counts.absent || 0} faltaron · {counts.makeup || 0} reposición
            </div>
            <button disabled={saving} onClick={save}
              className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
              {saving ? 'Guardando…' : 'Guardar lista'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
